/**
 * The formatter a field wants, when nobody has said otherwise.
 *
 * A schema already knows that a field is money in sterling, or a date the host
 * writes as `d MMMM yyyy`, or a fraction meant to be read as a percentage.
 * Nothing was doing anything with that: a binding inserted from the data panel
 * was the bare path, so a currency field rendered as `482310.4` and a date as
 * `2026-08-31T00:00:00.000Z` until somebody typed a pipe after it.
 *
 * That is a fine default for a developer who knows the expression language and
 * a poor one for anybody else, so the pipe is written into the expression at the
 * moment the binding is created. Written in, and not applied invisibly at render
 * time, for two reasons worth stating:
 *
 * - it stays visible and editable. The template says `| currency:'USD'`, so
 *   somebody reading it can see why the document shows dollars, and somebody who
 *   wants three decimal places can change it;
 * - it cannot change what an existing template does. Formatting bound values at
 *   render time would alter the output of every document already saved against a
 *   typed schema, which is not a change a published SDK gets to make quietly.
 *
 * Blocks that evaluate their own expressions — a table's columns, a chart's
 * series — get the same treatment from the same function, which is what keeps a
 * total in a table footer formatted like the column above it.
 */
import type { FieldSchema } from "../types/data.js";

/**
 * The pipe expression for a field, or null when the value should be shown as it
 * is.
 *
 * Text, booleans, URLs and rich text are already what they should look like.
 * Everything numeric or temporal is not.
 */
export function defaultPipeFor(schema: FieldSchema | undefined): string | null {
  if (!schema) return null;
  switch (schema.type) {
    case "currency": {
      // The code is quoted into the expression rather than left to the render
      // context, because a document's currency and a column's currency are
      // different facts: a GBP report can perfectly well contain a line in
      // dollars, and the context can only hold one answer.
      const code = schema.currency?.trim().toUpperCase();
      return code ? `currency:'${code}'` : "currency";
    }
    case "percent":
      return "percent";
    case "number":
      // Thousands separators and the locale's decimal point. A number with no
      // pipe prints as JavaScript writes it, which is 1234.5 where a document
      // wants 1,234.5.
      return "number";
    case "date":
      return schema.format ? `date:'${escapePattern(schema.format)}'` : "date";
    case "datetime":
      return schema.format ? `datetime:'${escapePattern(schema.format)}'` : "datetime";
    default:
      return null;
  }
}

/**
 * Adds the field's default formatter to a path expression.
 *
 * Leaves anything that already has a pipe alone — the author said what they
 * wanted — and leaves anything that is not a plain path alone too, because a
 * computed expression's type is its own business and appending to it could
 * change what it means.
 */
export function withDefaultPipe(expr: string, schema: FieldSchema | undefined): string {
  const trimmed = expr.trim();
  if (!trimmed || hasPipe(trimmed)) return trimmed;
  if (!isPlainPath(trimmed)) return trimmed;
  const pipe = defaultPipeFor(schema);
  return pipe ? `${trimmed} | ${pipe}` : trimmed;
}

/** Whether an expression already ends in a formatter, ignoring `||`. */
export function hasPipe(expr: string): boolean {
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] !== "|") continue;
    if (expr[i + 1] === "|") {
      i++;
      continue;
    }
    if (expr[i - 1] === "|") continue;
    return true;
  }
  return false;
}

/**
 * A dotted path and nothing else: `client.name`, `row.amount`, `holdings[0].value`.
 *
 * Anything with an operator, a call or a literal in it is left alone. `a + b`
 * with a currency pipe bolted on would format the sum, which may be what the
 * author wanted or may not, and guessing is worse than doing nothing.
 */
export function isPlainPath(expr: string): boolean {
  return /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*|\[\d+\])*$/.test(expr.trim());
}

/** A single quote inside a pattern would end the string literal early. */
function escapePattern(pattern: string): string {
  return pattern.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}
